import React, { useEffect, useRef, useState, type ElementRef } from 'react';
import { View, Text, ScrollView, StyleSheet, TouchableOpacity } from 'react-native';
import { Image } from 'expo-image';
import { ChevronRight, GitCompareArrows, X } from 'lucide-react-native';
import { useTheme, SPACING, BORDER_RADIUS, TYPOGRAPHY } from '../../theme';
import { usePanelStack } from '../layout/PanelStack';
import { usePostsStore } from '../../store/postsStore';
import { formatRelativeTime } from '../../utils/time';
import type { Post } from '../../types';

interface Props {
  structureId: string | undefined;
  value: Post | null;
  onChange: (post: Post | null) => void;
}

/**
 * "Pair with a before photo" row + drill-in picker. Lets the poster link
 * this observation to an earlier post on the same structure, producing a
 * before/after pair (the kind of evidence insurers ask for). Renders nothing
 * without a linked structure — pairing is structure-scoped.
 *
 * The picker is a PanelStack push (panel-system.md Phase 3) — this section
 * must therefore only ever render inside PostComposer's PanelStack.
 */
export function BeforePostSection({ structureId, value, onChange }: Props) {
  const { colors } = useTheme();
  const { push, pop } = usePanelStack();
  const rowRef = useRef<ElementRef<typeof TouchableOpacity>>(null);
  const [candidates, setCandidates] = useState<Post[]>([]);
  const fetchBeforeCandidates = usePostsStore((s) => s.fetchBeforeCandidates);

  useEffect(() => {
    let stale = false;
    setCandidates([]);
    if (!structureId) return;
    fetchBeforeCandidates(structureId)
      .then((posts) => {
        if (!stale) setCandidates(posts);
      })
      .catch(() => {
        // No candidates — the row just doesn't render.
      });
    return () => {
      stale = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- store action is stable
  }, [structureId]);

  if (!value && (!structureId || candidates.length === 0)) return null;

  const openPicker = () => {
    push({
      key: 'before-post',
      title: 'Pick the "before" photo',
      triggerRef: rowRef,
      render: () => (
        <BeforePostPickerBody
          candidates={candidates}
          onSelect={(post) => {
            onChange(post);
            pop();
          }}
        />
      ),
    });
  };

  return (
    <>
      {value ? (
        <View
          testID="before-linked-row"
          style={[styles.selectedRow, { borderBottomColor: colors.border }]}>
          {/* visualmask: live image + relative time, flattened by the visual
              audit's mask CSS (no effect outside the audit). */}
          <View
            style={styles.selectedThumb}
            // @ts-expect-error dataSet is a react-native-web prop (visual-audit CSS masking hook); not in RN's type defs
            dataSet={{ visualmask: 'true' }}>
            <Image
              source={value.thumbnailUrl || value.imageUrl}
              style={[styles.selectedThumbImage, { backgroundColor: colors.border }]}
              contentFit="cover"
              cachePolicy="memory-disk"
            />
          </View>
          <View style={styles.selectedText}>
            <Text style={[styles.selectedTitle, { color: colors.text }]}>Before photo linked</Text>
            <Text
              style={[styles.selectedMeta, { color: colors.textSecondary }]}
              numberOfLines={1}
              // @ts-expect-error dataSet is a react-native-web prop (visual-audit CSS masking hook); not in RN's type defs
              dataSet={{ visualmask: 'true' }}>
              {formatRelativeTime(value.createdAt, { suffix: 'ago' })}
              {value.caption ? ` · ${value.caption}` : ''}
            </Text>
          </View>
          <TouchableOpacity
            onPress={() => onChange(null)}
            hitSlop={8}
            accessibilityRole="button"
            accessibilityLabel="Remove before photo link">
            <X size={16} color={colors.textTertiary} />
          </TouchableOpacity>
        </View>
      ) : (
        <TouchableOpacity
          ref={rowRef}
          onPress={openPicker}
          activeOpacity={0.7}
          style={[styles.row, { borderBottomColor: colors.border }]}>
          <GitCompareArrows size={16} color={colors.text} />
          <Text style={[styles.rowText, { color: colors.textSecondary }]} numberOfLines={1}>
            Pair with a “before” photo (optional)
          </Text>
        </TouchableOpacity>
      )}
      <Text style={[styles.hint, { color: colors.textSecondary }]}>
        {value
          ? 'This post will show as the “after” next to it'
          : 'Show the progress since an earlier observation here'}
      </Text>
    </>
  );
}

interface BodyProps {
  candidates: Post[];
  onSelect: (post: Post) => void;
}

function BeforePostPickerBody({ candidates, onSelect }: BodyProps) {
  const { colors } = useTheme();

  return (
    <ScrollView testID="before-post-picker-sheet" contentContainerStyle={styles.sheetContent}>
      <Text style={[styles.sheetBlurb, { color: colors.textSecondary }]}>
        Earlier observations on this property
      </Text>

      {/* Hairline rows (mockup .rowlink), not bordered boxes. */}
      <View style={[styles.candidates, { borderTopColor: colors.border }]}>
        {candidates.map((post) => (
          <TouchableOpacity
            key={post.id}
            onPress={() => onSelect(post)}
            accessibilityRole="button"
            accessibilityLabel={post.caption || 'Observation with no caption'}
            style={[styles.candidateRow, { borderBottomColor: colors.border }]}>
            <Image
              source={post.thumbnailUrl || post.imageUrl}
              style={[styles.candidateThumb, { backgroundColor: colors.border }]}
              contentFit="cover"
              cachePolicy="memory-disk"
            />
            <View style={styles.candidateText}>
              <Text style={[styles.candidateCaption, { color: colors.text }]} numberOfLines={2}>
                {post.caption || 'No caption'}
              </Text>
              <Text style={[styles.candidateMeta, { color: colors.textSecondary }]}>
                {formatRelativeTime(post.createdAt, { suffix: 'ago' })}
                {post.isAnonymous ? ' · Fire Scout' : ` · ${post.authorName}`}
              </Text>
            </View>
            <ChevronRight size={14} color={colors.textTertiary} />
          </TouchableOpacity>
        ))}
      </View>
      {/* Scroll sentinel per sheet conventions. */}
      <View testID="before-post-picker-sheet-end" style={styles.scrollSentinel} />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  // Hairline rows, not tinted boxes (mockup .rowlink).
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: SPACING.smd,
    gap: SPACING.sm,
    borderBottomWidth: 1,
  },
  rowText: {
    ...TYPOGRAPHY.bodySmall,
    flex: 1,
  },
  hint: {
    ...TYPOGRAPHY.bodySmall,
    marginTop: SPACING.xs,
    marginBottom: SPACING.md,
  },
  selectedRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: SPACING.sm,
    gap: SPACING.sm,
    borderBottomWidth: 1,
  },
  selectedThumb: {
    width: 40,
    height: 40,
    borderRadius: BORDER_RADIUS.sm,
    overflow: 'hidden',
  },
  selectedThumbImage: {
    width: '100%',
    height: '100%',
  },
  selectedText: {
    flex: 1,
    gap: 1,
  },
  selectedTitle: {
    ...TYPOGRAPHY.label,
  },
  selectedMeta: {
    ...TYPOGRAPHY.bodySmall,
  },
  sheetContent: {
    padding: SPACING.lg,
    paddingBottom: SPACING.xxl,
  },
  sheetBlurb: {
    ...TYPOGRAPHY.bodySmall,
    marginBottom: SPACING.md,
  },
  candidates: {
    borderTopWidth: 1,
  },
  candidateRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.smd,
    paddingVertical: SPACING.sm,
    borderBottomWidth: 1,
  },
  candidateThumb: {
    width: 56,
    height: 56,
    borderRadius: BORDER_RADIUS.sm,
  },
  candidateText: {
    flex: 1,
    gap: 2,
  },
  candidateCaption: {
    ...TYPOGRAPHY.label,
  },
  candidateMeta: {
    ...TYPOGRAPHY.bodySmall,
  },
  scrollSentinel: {
    height: 1,
  },
});
